import { Trans, useTranslation } from 'next-i18next';
import { Fragment, memo, useEffect, useRef } from 'react';

import { Picture } from '../components/picture';
import { TrialCta } from '../components/trial-cta';
import { CONTAINER_CLASS, EYEBROW_CLASS, GOLD_EM_CLASS, SECTION_CLASS, SECTION_HEAD_CLASS, SECTION_TITLE_CLASS, SectionBackground, Stars } from '../components/ui';
import type { ChampionSlug } from '../constants';
import { CHAMPIONS, FINAL_FORM_ANCHOR, V4A_NAMESPACE } from '../constants';
import { startChampions } from '../engines/champions-engine';

const CHAMP_CLASS = 'champ relative flex min-w-0 flex-col gap-4 overflow-hidden p-[clamp(1.15rem,_.85rem_+_1.1vw,_1.75rem)] [box-shadow:var(--shadow-md)]';
const DISC_CLASS = 'champ__disc champ__swap m-0 flex min-w-0 items-center gap-[.55rem] font-mono text-[length:var(--fs-2xs)] font-medium uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--gold)]';
const RUNE_CLASS = 'champ__rune h-[9px] w-[9px] flex-none bg-[color:var(--gold)] [box-shadow:0_0_10px_rgb(var(--gold-rgb)_/_.7)]';
const FIX_CLASS = 'champ__fix champ__swap m-0 grid min-w-0 gap-[.3rem] rounded-e-[var(--r-sm)] border-s-2 border-solid border-s-[color:rgb(var(--accent-rgb)_/_.55)] p-3 ps-4 text-[length:var(--fs-sm)] leading-[1.5] text-[color:var(--fg-dim)]';
const FIX_LABEL_CLASS = 'font-mono text-[length:var(--fs-2xs)] font-medium uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--accent)]';
const DOT_CLASS = 'champ__dot grid h-11 cursor-pointer items-center justify-items-center rounded-[var(--r-sm)] border-0 p-0 [--p:1] [background:none]';

/** The lead player's line after "Contra-feitiço", with the source's line break kept as a real <br>. */
function FixText({ slug }: { slug: ChampionSlug }) {
  const { t } = useTranslation(V4A_NAMESPACE);
  const lines = t(`champions.players.${slug}.fix`).split('\n');
  return (
    <>
      {lines.map((line, index) => (
        <Fragment key={line}>
          {index > 0 && <br />}
          {line}
        </Fragment>
      ))}
    </>
  );
}

function SmallChampion({ slug }: { slug: ChampionSlug }) {
  const { t } = useTranslation(V4A_NAMESPACE);
  const player = CHAMPIONS.find((champion) => champion.slug === slug);
  return (
    <article className={CHAMP_CLASS}>
      <p className={DISC_CLASS}>
        <i className={RUNE_CLASS} aria-hidden='true' />
        {t(`champions.players.${slug}.game`)}
      </p>
      <blockquote className='champ__q champ__swap m-0 min-w-0'>
        <p className='champ__pull m-0 text-pretty text-[length:clamp(1.02rem,_.95rem_+_.35vw,_1.22rem)] font-semibold leading-[1.34] text-[color:var(--fg-strong)]'>{t(`champions.players.${slug}.pull`)}</p>
        <p className='champ__rest m-0 mt-[.55rem] text-[length:var(--fs-sm)] leading-[1.6] text-[color:var(--fg-dim)]'>{t(`champions.players.${slug}.rest`)}</p>
      </blockquote>
      <p className={FIX_CLASS}>
        <span className={FIX_LABEL_CLASS}>{t('champions.fix_label')}</span>
        <FixText slug={slug} />
      </p>
      <footer className='champ__who champ__swap mt-auto flex min-w-0 items-center gap-3 border-t border-solid border-t-[color:var(--rim)] pt-4'>
        <span className='champ__face block size-8 flex-none rounded-[50%] p-[2px] [box-shadow:0_0_0_1px_rgb(8_6_15_/_.9),_0_0_14px_rgb(var(--gold-rgb)_/_.2)]'>
          <Picture name={`player-${slug}-face`} widths={[64, 128]} sizes='2rem' alt='' width={128} height={128} loading='lazy' className='np-player-avatar block size-full rounded-[50%] bg-[color:#14161B] object-cover object-[50%_32%]' />
        </span>
        <span className='champ__id m-0 grid min-w-0 gap-[.12rem]'>
          <b className='text-[length:var(--fs-md)] font-bold text-[color:var(--fg-strong)]'>{player?.name}</b>
          <small className='text-[length:var(--fs-xs)] leading-[1.4] text-[color:var(--fg-dim)]'>{t(`champions.players.${slug}.title`)}</small>
        </span>
      </footer>
    </article>
  );
}

function ChampionsCards() {
  const { t } = useTranslation(V4A_NAMESPACE);
  const lead = CHAMPIONS[0];
  if (!lead) return null;
  return (
    <div className='champs grid gap-[clamp(.85rem,_.5rem_+_1.2vw,_1.5rem)] min-[752px]:grid-cols-2' data-reveal-group=''>
      <article className='champ champ--lead relative flex min-w-0 flex-col gap-[clamp(1.1rem,_.9rem_+_1vw,_1.75rem)] overflow-hidden p-[clamp(1.15rem,_.85rem_+_1.1vw,_1.75rem)] [box-shadow:var(--shadow-md)] min-[544px]:grid min-[544px]:grid-cols-[minmax(0,_min(19rem,_34%))_minmax(0,_1fr)] min-[544px]:items-start min-[544px]:gap-x-[clamp(1.25rem,_.9rem_+_1.6vw,_2.25rem)] min-[752px]:col-span-full lg:grid-cols-[minmax(0,_min(19rem,_26%))_minmax(0,_1fr)_minmax(0,_min(19.5rem,_30%))] lg:items-center'>
        <div className='champ__side flex min-w-0 flex-col gap-3 min-[544px]:row-[1_/_span_2] lg:row-[1]'>
          <div className='champ__art champ__swap relative aspect-[3/2] w-full min-w-0 max-w-[304px] overflow-hidden rounded-[var(--r-md)] border border-solid border-[color:rgb(var(--gold-rgb)_/_.24)] [box-shadow:0_0_30px_rgb(var(--gold-rgb)_/_.12)]'>
            <Picture name={`player-${lead.slug}-art`} widths={[480, 960]} sizes='304px' alt='' width={1536} height={1024} loading='lazy' className='block size-full object-contain object-center' />
          </div>
          <p className='champ__id champ__id--lead champ__swap m-0 grid min-w-0 gap-[.12rem] border-t border-solid border-t-[color:var(--rim)] pt-3'>
            <b className='text-[length:clamp(1.05rem,_.95rem_+_.45vw,_1.3rem)] font-bold text-[color:var(--fg-strong)]'>{lead.name}</b>
            <small className='text-[length:var(--fs-xs)] leading-[1.4] text-[color:var(--fg-dim)]'>{t(`champions.players.${lead.slug}.title`)}</small>
          </p>
          <div className='champ__nav min-w-0 flex-wrap items-center justify-between gap-2'>
            <div className='champ__dots inline-flex h-11 items-center rounded-full border border-solid border-[color:var(--rim)] bg-[color:rgb(8_6_15_/_.55)] px-0.5 py-0 min-[360px]:px-[.35rem] min-[360px]:py-0' role='group' aria-label={t('champions.dots_label')}>
              {CHAMPIONS.map((champion, index) => (
                <button key={champion.slug} className={DOT_CLASS} type='button' data-champ-dot={index} aria-pressed={index === 0 ? 'true' : 'false'} aria-label={t('champions.highlight', { name: champion.name })}>
                  <span className='champ__dotbar relative block h-[6px] overflow-hidden rounded-full'>
                    <span className='champ__dotfill absolute inset-0 rounded-[inherit] bg-[color:var(--gold)] [box-shadow:0_0_10px_rgb(var(--gold-rgb)_/_.6)]' />
                  </span>
                </button>
              ))}
            </div>
            <button className='mochip champ__pause min-h-11 min-w-11 flex-none items-center justify-center rounded-[var(--r-md)] border-0 p-0 [background:none]' type='button' data-champ-pause='' aria-pressed='false' aria-label={t('champions.pause')}>
              <span className='mochip__in inline-flex min-h-9 items-center gap-[.45rem] px-3 py-0 text-[length:var(--fs-xs)] font-semibold text-inherit'>
                <svg className='ic-pause' width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' aria-hidden='true' focusable='false'>
                  <path d='M8.5 5.5v13M15.5 5.5v13' />
                </svg>
                <svg className='ic-play' width='15' height='15' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' focusable='false'>
                  <path d='M8 5.2v13.6a.8.8 0 0 0 1.2.7l11-6.8a.8.8 0 0 0 0-1.4l-11-6.8a.8.8 0 0 0-1.2.7z' />
                </svg>
                <span data-pauselbl=''>{t('controls.pause')}</span>
              </span>
            </button>
          </div>
        </div>
        <div className='champ__body champ__swap flex min-w-0 flex-col gap-4'>
          <p className='champ__disc m-0 flex min-w-0 items-center gap-[.55rem] font-mono text-[length:var(--fs-2xs)] font-medium uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--gold)]'>
            <i className={RUNE_CLASS} aria-hidden='true' />
            {t(`champions.players.${lead.slug}.game`)}
          </p>
          <blockquote className='champ__q m-0 min-w-0'>
            <p className='champ__pull m-0 text-pretty text-[length:clamp(1.15rem,_.98rem_+_.8vw,_1.6rem)] font-semibold leading-[1.24] text-[color:var(--fg-strong)]'>{t(`champions.players.${lead.slug}.pull`)}</p>
            <p className='champ__rest m-0 mt-[.55rem] text-[length:var(--fs-md)] leading-[1.6] text-[color:var(--fg-dim)]'>{t(`champions.players.${lead.slug}.rest`)}</p>
          </blockquote>
        </div>
        <div className='champ__rail flex min-w-0 flex-col justify-center gap-4 self-stretch lg:border-s lg:border-solid lg:border-s-[color:var(--rim)] lg:ps-[clamp(1.25rem,_.9rem_+_1.4vw,_2rem)]'>
          <p className={FIX_CLASS}>
            <span className={FIX_LABEL_CLASS}>{t('champions.fix_label')}</span>
            <FixText slug={lead.slug} />
          </p>
          <TrialCta className='btn btn-primary champ__cta relative isolate inline-flex min-h-[var(--x-h)] min-w-0 items-center justify-center gap-2 self-stretch whitespace-normal px-4 py-0 text-center text-[length:var(--fs-sm)] font-bold uppercase leading-[1.2] tracking-[.06em] no-underline [--x-h:3.25rem]' anchor={FINAL_FORM_ANCHOR}>
            {t('cta.try_free')}
          </TrialCta>
          <p className='champ__note m-0 min-w-0 text-[length:var(--fs-xs)] leading-normal text-[color:var(--fg-dim)]'>{t('champions.note')}</p>
        </div>
      </article>
      {CHAMPIONS.slice(1).map((champion) => (
        <SmallChampion key={champion.slug} slug={champion.slug} />
      ))}
    </div>
  );
}

/** The engine owns the rotating texts: React renders this subtree once. */
const ChampionsCardsOnce = memo(ChampionsCards);

/** "O contra-feitiço escolhido pelos campeões": rating plate and the rotating champion cards. */
export function Champions() {
  const { t } = useTranslation(V4A_NAMESPACE);
  const holdRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrap = holdRef.current?.querySelector<HTMLElement>('.champs');
    if (!wrap) return undefined;
    return startChampions(wrap, { labelPause: t('champions.pause'), labelResume: t('champions.resume'), textPause: t('controls.pause'), textResume: t('controls.resume') });
  }, [t]);

  return (
    <section className={`${SECTION_CLASS} sec--pros`} id='campeoes' aria-labelledby='pros-title'>
      <SectionBackground
        halos={[
          { position: 'right', tone: 'gold' },
          { position: 'left', tone: 'violet' },
        ]}
      />
      <div ref={holdRef} className={CONTAINER_CLASS}>
        <div className={`${SECTION_HEAD_CLASS} mb-[clamp(2rem,_1rem_+_3vw,_3.5rem)]`} data-reveal=''>
          <div className='pros__intro grid min-w-0 content-start gap-3'>
            <p className={EYEBROW_CLASS}>{t('champions.eyebrow')}</p>
            <h2 className={SECTION_TITLE_CLASS} id='pros-title'>
              <Trans t={t} i18nKey='champions.title' components={{ nowrap: <span className='whitespace-nowrap' />, em: <em className={GOLD_EM_CLASS} /> }} />
            </h2>
            <p className='sec__lead m-0 mt-2 max-w-[34em] text-[length:var(--fs-lg)] leading-[1.6] text-[color:var(--fg-muted)]'>{t('champions.lead')}</p>
          </div>
          <div className="rating grid min-w-0 content-center justify-items-start gap-[.35rem] rounded-[var(--r-lg)] border border-solid border-[color:rgb(var(--gold-rgb)_/_.3)] px-[clamp(1.1rem,_.85rem_+_.9vw,_1.6rem)] py-[clamp(1rem,_.8rem_+_.7vw,_1.4rem)] [box-shadow:var(--shadow-md)] min-[480px]:grid-cols-[auto_minmax(0,_1fr)] min-[480px]:items-center min-[480px]:gap-x-[clamp(1rem,_.8rem_+_.9vw,_1.5rem)] min-[480px]:gap-y-[.3rem] min-[480px]:[grid-template-areas:'n_stars'_'n_copy'] lg:max-w-[30rem] lg:justify-self-end forced-colors:border-[color:CanvasText]" data-count-group=''>
            <Stars size={17} label={t('rating.label')} className='gap-[.15rem] min-[480px]:self-end min-[480px]:[grid-area:stars]' />
            <p className='rating__n m-0 whitespace-nowrap text-[length:clamp(1.75rem,_1.4rem_+_1.2vw,_2.35rem)] font-bold leading-none tracking-[-.02em] text-[color:var(--fg-strong)] min-[480px]:self-center min-[480px]:[grid-area:n]'>
              <span className='cntbox inline-block whitespace-nowrap text-start normal-case tracking-[inherit] text-inherit' aria-hidden='true'>
                <span className='cnt normal-case tracking-[inherit] text-inherit' data-count-to='4.9' data-count-dec='1'>
                  {t('rating.value')}
                </span>
                <span className='text-[length:.5em] font-semibold text-[color:var(--fg-dim)]'>/5</span>
              </span>
              <span className='sr-only'>{t('rating.spoken')}</span>
            </p>
            <p className='rating__c m-0 max-w-[26em] text-[length:var(--fs-xs)] leading-normal text-[color:var(--fg-dim)] min-[480px]:self-start min-[480px]:[grid-area:copy]'>
              <Trans t={t} i18nKey='champions.rating_copy' components={{ b: <b className='font-semibold text-[color:var(--fg)]' /> }} />
            </p>
          </div>
        </div>

        <ChampionsCardsOnce />
      </div>
    </section>
  );
}
